import {
  Box, Button, Checkbox, FormControl, FormControlLabel, Grid, Modal, Stack, TextField, Typography,
} from '@mui/material';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import { useEffect, useMemo, useState } from 'react';
import BlockData from '../../../models/BlockData';
import CarouselItem from './partials/CarouselItem';
import BlockCarouselItemData from '../../Blocks/BlockCarousel/BlockCarouselItemData';
import { ColorPickerColorsMap } from '../../../models/ColorPicker';
import config from '../../../config';

const style = {
  position: 'absolute',
  overflow: 'auto',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  height: '90%',
  maxWidth: '90%',
  maxHeight: '90%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 4,
};

interface CarouselModalProps {
  carousel: BlockCarouselItemData[];
  slideId: string;
  auto: boolean;
  autoInterval: number;
  open: boolean;
  handleClose: () => void;
  handleSave: (newItems: BlockCarouselItemData[], auto: boolean, autoInterval: number) => void;
  blockData: BlockData[];
}

function CarouselModal({
  carousel, slideId, auto, open, blockData, handleClose, handleSave, autoInterval: autoIntervalDefault,
}: CarouselModalProps) {
  const [managedItems, setManagedItems] = useState<BlockCarouselItemData[]>(carousel);
  const [managedAuto, setManagedAuto] = useState<boolean>(auto);
  const [autoInterval, setAutoInterval] = useState<number>(autoIntervalDefault);

  const handleAdd = () => {
    setManagedItems([...managedItems, BlockCarouselItemData.getDefault(config.defaultImageUrl)]);
  };

  const items = useMemo(() => {
    if (managedItems.length > 0) {
      return managedItems.map((carouselItem, index) => (
        <Grid key={carouselItem.id} container direction="row">

          <Grid item xs={1}>
            <Box
              sx={{
                backgroundColor: ColorPickerColorsMap.indigo,
                height: '100%',
                paddingTop: 2,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography color="white">
                {index + 1}
                枚目
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={11}>
            <CarouselItem
              key={carouselItem.id}
              item={carouselItem}
              blockData={blockData}
              isTemplate={false}
              isEditing
              onRemoveBlock={() => { setManagedItems(managedItems.filter((item) => item !== carouselItem)); }}
              onChangeImage={(image) => {
                managedItems[index].image.value = image;
                setManagedItems([...managedItems]);
              }}
              onChangeLink={(link) => {
                managedItems[index].link = link;
                setManagedItems([...managedItems]);
              }}
              onChangeAlt={(alt) => {
                managedItems[index].image.alt = alt;
                setManagedItems([...managedItems]);
              }}
            />
          </Grid>
        </Grid>
      ));
    }

    return [];
  }, [blockData, managedItems]);

  useEffect(() => {
    if (!open) {
      setManagedItems(carousel);
      setManagedAuto(auto);
    }
  }, [carousel, open, auto]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
    >
      <Box sx={style}>
        <Stack spacing={4}>
          {items}

          <Stack key="add" spacing={0} sx={{ border: '1px dashed lightGray', p: 0 }}>
            <Button color="primary" onClick={handleAdd}>
              <AddCircleRoundedIcon />
            </Button>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Button variant="outlined" color="primary" onClick={() => handleClose()}>
              キャンセル
            </Button>

            <FormControlLabel
              label="自動再生をONにする"
              control={(
                <Checkbox
                  className={`my-slide-toggle-${slideId}`}
                  checked={managedAuto}
                  onChange={(event) => setManagedAuto(event.target.checked)}
                />
              )}
            />

            <FormControl>
              <TextField
                fullWidth
                type="number"
                label="自動再生間隔"
                value={autoInterval}
                onChange={(e) => {
                  setAutoInterval(parseInt(e.target.value, 10));
                }}
                helperText="秒数を入れてください"
                variant="outlined"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
            </FormControl>

            <Button variant="contained" color="primary" onClick={() => handleSave(managedItems, managedAuto, autoInterval)}>
              保存する
            </Button>
          </Stack>

          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Box sx={{ width: 'auto' }}>
              <Typography variant="subtitle2" color="grey">
                ※ 実際のスライダーの動きは、プレビュー/公開ページでご確認ください
              </Typography>
              <Typography variant="subtitle2" color="grey">
                ※ 比率が異なる場合は画像の一部が見切れたり余白が表示されます
              </Typography>
            </Box>
          </Stack>

        </Stack>
      </Box>
    </Modal>
  );
}

export default CarouselModal;
